import React from 'react';
import Icon from '../../../components/AppIcon';

const ClientStatsCard = ({ stats }) => {
  const formatPercentage = (value) => {
    return `${Math.round(value)}%`;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'text-success';
    if (percentage >= 60) return 'text-warning';
    return 'text-error';
  };

  const core4Areas = [
    { key: 'faith', label: 'Faith', icon: 'Heart' },
    { key: 'family', label: 'Family', icon: 'Users' },
    { key: 'finances', label: 'Finances', icon: 'DollarSign' },
    { key: 'fitness', label: 'Fitness', icon: 'Activity' }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Overall Program Stats */}
      <div className="bg-card border border-border rounded-lg shadow-gentle p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="TrendingUp" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Program Overview
            </h3>
            <p className="text-sm text-muted-foreground">
              Aggregate client statistics
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-2xl font-heading font-bold text-foreground">
              {stats?.totalClients}
            </div>
            <div className="text-sm text-muted-foreground">Total Clients</div>
          </div>
          
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-2xl font-heading font-bold text-foreground">
              {stats?.activeClients}
            </div>
            <div className="text-sm text-muted-foreground">Active This Week</div>
          </div>
          
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-2xl font-heading font-bold text-primary">
              {formatPercentage(stats?.averageCompletion)}
            </div>
            <div className="text-sm text-muted-foreground">Avg Completion</div>
          </div>
          
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-2xl font-heading font-bold text-foreground">
              {stats?.curriculumCompletions}
            </div>
            <div className="text-sm text-muted-foreground">Completed 42 Days</div>
          </div>
        </div>
      </div>
      {/* CORE4 Completion Rates */}
      <div className="bg-card border border-border rounded-lg shadow-gentle p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg">
            <Icon name="Target" size={20} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              CORE4 Performance
            </h3>
            <p className="text-sm text-muted-foreground">
              Average completion rates across all clients
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {core4Areas?.map((area) => {
            const areaStats = stats?.core4Stats?.[area?.key];
            const completionRate = (areaStats?.completed / areaStats?.total) * 100;
            
            return (
              <div key={area?.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name={area?.icon} size={16} className="text-muted-foreground" />
                    <span className="font-caption font-medium text-foreground">
                      {area?.label}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className={`font-heading font-semibold ${getProgressColor(completionRate)}`}>
                      {formatPercentage(completionRate)}
                    </span>
                    <div className="text-xs text-muted-foreground">
                      {areaStats?.completed}/{areaStats?.total}
                    </div>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      completionRate >= 80 ? 'bg-success' :
                      completionRate >= 60 ? 'bg-warning' : 'bg-error'
                    }`}
                    style={{ width: `${Math.min(completionRate, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Engagement Metrics */}
      <div className="bg-card border border-border rounded-lg shadow-gentle p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
            <Icon name="Calendar" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Engagement Metrics
            </h3>
            <p className="text-sm text-muted-foreground">
              Daily and weekly activity patterns
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-xl font-heading font-bold text-foreground">
              {formatPercentage(stats?.dailyEngagement)}
            </div>
            <div className="text-sm text-muted-foreground">Daily Check-ins</div>
          </div>
          
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-xl font-heading font-bold text-foreground">
              {stats?.averageStreak}
            </div>
            <div className="text-sm text-muted-foreground">Avg Streak Days</div>
          </div>
        </div>

        <div className="mt-4 p-4 bg-primary/5 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-primary" />
            <span className="text-sm font-caption font-medium text-primary">
              Weekly Trends
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            Most active day: <span className="font-medium text-foreground">{stats?.mostActiveDay}</span>
            <br />
            Peak engagement time: <span className="font-medium text-foreground">{stats?.peakEngagementTime}</span>
          </div>
        </div>
      </div>
      {/* Goal Completion Insights */}
      <div className="bg-card border border-border rounded-lg shadow-gentle p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Goal Completion
            </h3>
            <p className="text-sm text-muted-foreground">
              Progress across different goal horizons
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="font-caption font-medium text-foreground">Daily Goals</span>
            </div>
            <span className="font-heading font-semibold text-success">
              {formatPercentage(stats?.goalCompletion?.daily)}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="font-caption font-medium text-foreground">90-Day Goals</span>
            </div>
            <span className="font-heading font-semibold text-warning">
              {formatPercentage(stats?.goalCompletion?.ninety)}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="font-caption font-medium text-foreground">5-Year Goals</span>
            </div>
            <span className="font-heading font-semibold text-primary">
              {formatPercentage(stats?.goalCompletion?.fiveYear)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientStatsCard;